import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/lib/cn';
import { surfaceClassName, useSurface } from './surface.context';

export const TooltipProvider = TooltipPrimitive.Provider;

interface TooltipProps {
  /** Text shown in the bubble. Falsy values show no bubble (same tree, so the trigger never remounts). */
  content: React.ReactNode;
  children: React.ReactElement;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Overrides the app-wide delay for this tooltip only. */
  delayDuration?: number;
  /** Suppresses the bubble without changing the React tree (no remount, no focus loss). */
  disabled?: boolean;
  className?: string;
}

/**
 * Tooltip bubble. Relies on the single <TooltipProvider> mounted in main.tsx,
 * so moving across adjacent triggers skips the delay.
 */
export function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  delayDuration,
  disabled = false,
  className,
}: TooltipProps) {
  const surface = useSurface();
  const [open, setOpen] = React.useState(false);
  return (
    <TooltipPrimitive.Root
      open={open && !disabled && Boolean(content)}
      onOpenChange={setOpen}
      delayDuration={delayDuration}
    >
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          data-slot="tooltip"
          side={side}
          align={align}
          sideOffset={6}
          className={cn(
            surfaceClassName(surface),
            'z-tooltip max-w-56 rounded-control bg-ink px-2 py-1 text-center text-xs font-medium text-surface shadow-e3',
            className,
          )}
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

/**
 * Disabled <button>s swallow pointer events, so a tooltip on them (typically
 * the "why is this disabled" reason) has to hang off a wrapper.
 */
export function DisabledTooltip({
  content,
  disabled,
  children,
  ...rest
}: Omit<TooltipProps, 'children'> & { disabled: boolean; children: React.ReactElement }) {
  if (!content || !disabled) return children;
  return (
    <Tooltip content={content} {...rest}>
      <span className="inline-flex">{children}</span>
    </Tooltip>
  );
}
